/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Canopy is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Canopy is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Canopy, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Canopy:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Canopy broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Canopy utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Canopy only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Canopy.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Canopy is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Canopy provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener('DOMContentLoaded', () => {
  let all_variant_picker = Array.from(
    document.querySelectorAll('product-card .variant_picker')
  );
  let all_quantity_input = Array.from(
    document.querySelectorAll('quantity-input input')
  );

  document.addEventListener('click', (e) => {
    all_variant_picker.forEach((variant_picker) => {
      if (variant_picker.contains(e.target)) {
        e.preventDefault();
        variant_picker.closest('product-card').querySelector('button').click();
      }
    });
  });

  document.addEventListener('change', (e) => {
    all_quantity_input.forEach((input) => {
      if (e.target == input && input.closest('.price_quantity')) {
        document
          .querySelector('.product-info__add-button')
          .parentElement.querySelector("input[name='quantity']").value =
          input.value;
      }
    });
  });
});

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.button = this.querySelector("button");
    this.discountMessage = this.querySelector(".discount_label");
    this.cartDrawerContainer = this.closest(".cart_drawer_container") || this.closest(".cart__summary");
    this.appliedDiscounts = this.cartDrawerContainer.dataset.discounts.length > 0 ? this.cartDrawerContainer.dataset.discounts[this.cartDrawerContainer.dataset.discounts.length - 1] == "," ? this.cartDrawerContainer.dataset.discounts.slice(0, -1).split(",") : this.cartDrawerContainer.dataset.discounts.split(",") : [];
    this.appliedDiscounts = this.appliedDiscounts.map(discount=>{
      if (discount != "") {
        return discount
      }
    });
    this.discountItems = this.cartDrawerContainer.querySelectorAll(".cart_discount_item");
  }

  connectedCallback() {
    if (this.cartDrawerContainer.hasAttribute("data-discount-message")) {
      this.showMessage("data-discount-message");
    }

    fetch('/cart.js')
    .then(response=>response.json())
    .then(cart=>{
      this.currentDiscounts = cart.cart_level_discount_applications.map(discount=>discount.title);

      if (this.closest("cart-drawer") && !this.closest("cart-drawer").classList.contains("initialized")) {
        this.closest("cart-drawer").classList.add("initialized");
      // } else if (this.closest("cart-drawer") && !this.closest("cart-drawer").classList.contains("updated")) {
      //   this.closest("cart-drawer").classList.add("updated");
      } else {
        if (this.currentDiscounts.length > this.appliedDiscounts.length) {
          let newDiscount = this.currentDiscounts.filter(item => !this.appliedDiscounts.includes(item));
          this.showMessage(`Discount '${newDiscount}' Applied!`);
  
        } else if (this.appliedDiscounts.length > this.currentDiscounts.length) {
          let removedDiscount = this.appliedDiscounts.filter(item => !this.currentDiscounts.includes(item));
          this.showMessage(`Discount '${removedDiscount}' Removed!`);
  
        } else if (this.currentDiscounts.length > 0 && JSON.stringify(this.currentDiscounts) == JSON.stringify(this.appliedDiscounts)) {
          if (this.cartDrawerContainer.hasAttribute("data-new-discount") && this.cartDrawerContainer.getAttribute("data-new-discount").length > 0) {
            this.showMessage(`'${this.cartDrawerContainer.getAttribute("data-new-discount")}' is not valid`);
          } else {
            this.showMessage("Discount Did Not Change!");
          }
  
        } else {
          let newDiscount = this.currentDiscounts.filter(item => !this.appliedDiscounts.includes(item));
          let removedDiscount = this.appliedDiscounts.filter(item => !this.currentDiscounts.includes(item));
          if (newDiscount != "") {
            this.showMessage(`Discount '${newDiscount}' Applied!`);
          } else if (removedDiscount != "") {
            this.showMessage(`Discount '${removedDiscount}' Removed!`);
          } else {
            if (this.cartDrawerContainer.hasAttribute("data-new-discount") && this.cartDrawerContainer.getAttribute("data-new-discount").length > 0) {
              this.showMessage(`'${this.cartDrawerContainer.getAttribute("data-new-discount")}' is not valid`);
            } else {
              this.showMessage("No Active Discounts");
            }
          }
        }
      }

      this.cartDrawerContainer.removeAttribute("data-new-discount");
      this.cartDrawerContainer.setAttribute("data-discounts", this.currentDiscounts.join(","));

      this.appliedDiscounts = this.cartDrawerContainer.dataset.discounts.length > 0 ? this.cartDrawerContainer.dataset.discounts[this.cartDrawerContainer.dataset.discounts.length - 1] == "," ? this.cartDrawerContainer.dataset.discounts.slice(0, -1).split(",") : this.cartDrawerContainer.dataset.discounts.split(",") : [];
      this.appliedDiscounts = this.appliedDiscounts.map(discount=>{
        if (discount != "") {
          return discount
        }
      });
    }).catch(error=>{
      console.log(error)
    }
  );

    this.discountItems.forEach(discountItem=>{
      discountItem.querySelector(".item_remove").addEventListener("click", ()=>{
        let newDiscounts = this.appliedDiscounts.map(discount=>{
          if (discount != discountItem.querySelector(".discount_item_label").textContent) {
            return discount;
          }
        })
        newDiscounts = newDiscounts.join(",");
        if (newDiscounts.length == 0) {
          this.submitDiscounts("clearAllAvailableDiscounts");
        } else {
          this.submitDiscounts(newDiscounts);
        }
      })
    })

    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.button.classList.add('loading');

      if (this.appliedDiscounts.includes(this.input.value)) {
        this.showMessage("Discount Already Applied");
      } else {
        let totalDiscounts = this.appliedDiscounts + "," + this.input.value
        this.cartDrawerContainer.setAttribute("data-new-discount", this.input.value);
        this.submitDiscounts(totalDiscounts);
      }
    })
  }

  submitDiscounts(totalDiscounts) {
    
    fetch(`/checkout?discount=${totalDiscounts}`, {method: 'GET'})
    .then(response=>response.text())
    .then(()=>{
      if (document.querySelector("cart-drawer")) {
        document.querySelector("cart-drawer").refreshCartDrawer();
      } else {
        window.location.href = window.location.href;
      }
    })
    .catch(error=>{
      console.log(error)
      if (document.querySelector("cart-drawer")) {
        document.querySelector("cart-drawer").refreshCartDrawer();
      } else {
        window.location.href = window.location.href;
      }
    })
  }

  showMessage(message) {
    message = message.replace("'", "<span>").replace("'", "</span>")
    this.discountMessage.classList.remove("hidden");
    this.discountMessage.innerHTML = message;
    this.hideMessage();
  }

  hideMessage() {
    setTimeout(()=>{
      this.discountMessage.classList.add("hidden");
      this.discountMessage.innerHTML = "";
      this.button.classList.remove("loading");
    }, 3000)
  }
}

customElements.define('cart-discount', CartDiscount);

class sliderNavigation extends HTMLElement {
  constructor() {
    super();
    this.prev = this.querySelector(`[name="prev"]`);
    this.next = this.querySelector(`[name="next"]`);
    this.section = this.closest('.shopify-section');
    this.slider = this.section.querySelector('carousel-slider');
    this.sliderPrev = this.slider.querySelector(`[name="prev"]`);
    this.sliderNext = this.slider.querySelector(`[name="next"]`);
  }

  connectedCallback() {
    this.prev.addEventListener('click', () => {
      this.sliderPrev.click();
    });
    this.next.addEventListener('click', () => {
      this.sliderNext.click();
    });
  }
}

customElements.define("slider-nav", sliderNavigation);


class LayoutItemSelector extends HTMLElement {
  constructor() {
    super();
    this.selector = this;
    this.optionContainer = this.querySelector('.item_count_options');
    this.options = this.querySelectorAll('.item_count_options a');
    this.output = this.querySelector('.active_item_count');
    this.defaultCount = this.dataset.defaultCount;
  }

  updateUrl() {
    let url = window.location.href;
    let fixedUrl =  url;
    
    if (url.includes("view=")) {
      let urlFirstPart = url.split("view=")[0].substring(0, url.split("view=")[0].length - 1);
      let urlLastPart = url.split("items")[1].length > 0 ? url.split("items")[1] : "";
      fixedUrl = urlFirstPart + urlLastPart;
      this.output.innerHTML = url.split('view=')[1].split('-')[0];
    }
    let newUrl = fixedUrl.includes('?')
      ? fixedUrl + '&view=x-items'
      : fixedUrl + '?view=x-items';
    this.options.forEach((option) => {
      option.setAttribute('href', newUrl.replace('=x-', `=${option.innerHTML}-`));
    });
  }

  updateSelector() {
    let totalProductCount = parseInt(document.querySelector(".total_product_count").innerHTML);

    if (totalProductCount < 32) {
      this.classList.remove("md:flex");
      this.classList.add("md:hidden");
      if (this.classList.contains("changed")) {
        this.classList.remove("changed");
      }
    } else {
      this.classList.remove("md:hidden");
      this.classList.add("md:flex");
    }
    
    if (totalProductCount >= 32 && totalProductCount < 48) {
      this.options[this.options.length - 1].classList.add("hidden");
      if (this.classList.contains("changed")) {
        this.classList.remove("changed");
      }
    } else {
      this.options[this.options.length - 1].classList.remove("hidden");
    }
  }

  connectedCallback() {
    this.updateUrl();
    this.updateSelector();

    document.addEventListener('on:facet-filters:updated', (e)=>{
      this.classList.add("changed");
      this.updateSelector();
    })

    document.addEventListener('click', (e) => {
      if (e.target.closest('layout-item-selector')) {
        this.updateUrl();
        this.optionContainer.classList.toggle('hidden');
      } else {
        this.optionContainer.classList.add('hidden');
      }
    });
  }
}

customElements.define('layout-item-selector', LayoutItemSelector);

Array.from(document.querySelectorAll("[name='quantity']")).forEach((qty) => {
  qty.addEventListener('blur', () => {
    if (qty.value < 1) {
      qty.value = 1;
    }
  });
});

document.addEventListener("scroll", ()=>{
  if (window.scrollY > 350) {
    if (document.querySelector(".overlay.is-visible")) document.querySelector(".overlay.is-visible").click()
  }
})

window.addEventListener('resize', () => {
  updateHeaderLogoSize();
  updateMegamenuTitleSize();
});
document.addEventListener('DOMContentLoaded', () => {
  updateHeaderLogoSize();
  setTimeout(() => {
    updateMegamenuTitleSize();
  }, 1000);
});
function updateHeaderLogoSize() {
  let header_logo_vertical_offset = '0px';
  let header_nav = document.querySelector('.main-menu__content_wrapper nav');
  let header_logo = document.querySelector('.logo__link');

  if (window.innerWidth > 768) {
    let logo_available_space =
      window.innerWidth - header_nav.offsetWidth - header_logo.offsetWidth * 2;
    if (logo_available_space < 100) {
      header_logo_vertical_offset = `${header_nav.offsetHeight}px`;
    } else {
      header_logo_vertical_offset = '0px';
    }
  } else if (window.innerWidth < 768) {
    header_logo.classList.add('updated');
  } else {
    header_logo_vertical_offset = '0px';
  }
  header_logo.classList.add('updated');
  document
    .querySelector('.header')
    .style.setProperty(
      '--header_logo_vertical_offset',
      header_logo_vertical_offset
    );
}
function updateMegamenuTitleSize() {
  let allMegaMenuItems = Array.from(
    document.querySelectorAll('.main-nav__child')
  );

  allMegaMenuItems.forEach((menu) => {
    let titleHeight = 0;
    menu.querySelectorAll('.child_nav_title').forEach((title) => {
      if (title.offsetHeight > titleHeight) titleHeight = title.offsetHeight;
    });
    menu
      .querySelectorAll('.child_nav_title')
      .forEach((title) => (title.style.height = `${titleHeight}px`));
  });
}
